import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, Select, Table, Badge, Modal, TextArea, Combobox } from '../components/Common';

/**
 * Requests: ask for a penetration test, and follow what happens to it.
 *
 * For an account whose only group is Requestors this is the whole application,
 * so it has to answer both questions on its own — what have we asked for, and
 * where has it got to — without linking anywhere else.
 */

const ENGAGEMENT_TYPES = [
  'Application Penetration Test',
  'API Penetration Test',
  'Mobile Application Test',
  'Network Penetration Test',
  'Red Team',
];

// Every status a request can pass through, in the order it passes through them,
// so the filter reads as a journey rather than an alphabetical list.
const STATUSES = ['Requested', 'New', 'Scoping', 'Contract Sent', 'In Progress',
                  'In Review', 'Complete', 'In Retest', 'Retest Complete', 'On Hold', 'Cancelled'];

// Types that test one identifiable application. A network test or a red team
// exercise covers an estate, so neither asks which application.
const APPLICATION_TYPES = ['Application Penetration Test', 'API Penetration Test', 'Mobile Application Test'];

/** Whole days from today until a date, negative once it has passed. */
const daysUntil = (v) => {
  if (!v) return null;
  const then = new Date(v); then.setHours(0, 0, 0, 0);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((then - now) / 86400000);
};

/** The deadline, and how much room is left before it. */
function NeededBy({ value }) {
  if (!value) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  const left = daysUntil(value);
  const colour = left < 0 ? '#dc2626' : left <= 14 ? '#ea580c' : undefined;
  return (
    <span style={{ color: colour, fontWeight: colour ? 600 : 400 }}>
      {new Date(value).toLocaleDateString()}
      <span style={{ fontSize: 12, fontWeight: 400, color: colour || 'var(--text-secondary)' }}>
        {left < 0 ? ` (${Math.abs(left)}d late)` : left === 0 ? ' (today)' : ` (${left}d)`}
      </span>
    </span>
  );
}

// The characteristics worth showing back. Only the ones that are true are
// listed: a column of "No" tells a requester nothing about whether this is the
// right system, and crowds out the ones that do.
const CHARACTERISTICS = [
  { key: 'internet_facing', label: 'Internet facing' },
  { key: 'pci_required', label: 'PCI in scope' },
  { key: 'payment_function', label: 'Takes payments' },
  { key: 'company_owned', label: 'Company owned' },
  { key: 'company_hosted', label: 'Company hosted' },
  { key: 'ip_whitelist_needed', label: 'Needs IP allow-listing' },
];

/** What the requester is about to ask us to test, so they can check it. */
function ApplicationSummary({ app }) {
  const traits = CHARACTERISTICS.filter(c => app[c.key]);
  const scope = Array.isArray(app.scope_items) ? app.scope_items : [];
  return (
    <div style={{ marginTop: -8, marginBottom: 16, padding: 14, borderRadius: 8,
                  background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>{app.name}</strong>
        {app.customer_tracking_id && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{app.customer_tracking_id}</span>
        )}
      </div>

      {traits.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {traits.map(t => (
            <span key={t.key} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px',
                                       borderRadius: 8, background: 'var(--bg-card, #1a1f2e)',
                                       border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
              {t.label}
            </span>
          ))}
        </div>
      )}

      {scope.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
            Recorded scope
          </div>
          <div style={{ maxHeight: 120, overflowY: 'auto' }}>
            {scope.map((si, i) => (
              <div key={i} style={{ fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    color: 'var(--text-secondary)', padding: '1px 0' }}>
                <span style={{ opacity: 0.7 }}>{si.scope_type}</span> {si.value}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 12 }}>
        {traits.length === 0 && scope.length === 0
          ? 'Nothing else is recorded against this application. Use the box below to tell us about it.'
          : 'Recorded by your organisation, shown so you can check this is the right system. ' +
            'It cannot be edited here — say anything that has changed in the box below.'}
      </div>
    </div>
  );
}

export default function Requests() {
  const { apiFetch, user, hasGroup } = useAuth();
  const navigate = useNavigate();

  const [engagements, setEngagements] = useState([]);
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ engagementType: 'Application Penetration Test' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState('');

  // Actual membership, not hasGroup: that answers true for Super Admins, and a
  // Super Admin looking at this page is reviewing what has come in rather than
  // following requests of their own.
  const isRequester = !!user?.groups?.includes('Requestors');

  // A Super Admin has no client of their own, so a request from one has to say
  // which client it is for.
  const needsClient = !user?.clientId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/engagements');
      const d = await res.json().catch(() => null);
      setEngagements(Array.isArray(d) ? d : []);
      if (!res.ok) setError(d?.error || 'Could not load tests');
      else setError('');
    } catch {
      setEngagements([]);
      setError('Could not load tests');
    }
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!needsClient) return;
    apiFetch('/clients').then(r => (r.ok ? r.json() : [])).then(d => {
      if (Array.isArray(d)) setClients(d.filter(c => c.is_active !== false));
    }).catch(() => {});
  }, [apiFetch, needsClient]);

  // Loaded per client so a provider-side requester picking a different client
  // gets that client's applications rather than a stale list.
  const appClientId = user?.clientId || form.clientId;
  useEffect(() => {
    if (!appClientId) { setApps([]); return; }
    apiFetch(`/applications/options/${appClientId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setApps(Array.isArray(d) ? d : []))
      .catch(() => setApps([]));
  }, [apiFetch, appClientId]);

  const submit = async () => {
    setFormError('');
    if (!form.name?.trim()) { setFormError('Tell us what should be tested'); return; }
    if (needsClient && !form.clientId) { setFormError('Choose a client'); return; }
    setSubmitting(true);
    const res = await apiFetch('/engagements/requests', {
      method: 'POST',
      body: JSON.stringify({
        engagementType: form.engagementType,
        name: form.name.trim(),
        description: form.description || null,
        reportNeededBy: form.reportNeededBy || null,
        applicationId: APPLICATION_TYPES.includes(form.engagementType) ? (form.applicationId || null) : null,
        ...(needsClient ? { clientId: form.clientId } : {}),
      }),
    });
    setSubmitting(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setFormError(d.error || `Could not submit the request (HTTP ${res.status})`);
      return;
    }
    setShowForm(false);
    setSubmitted(form.name.trim());
    setForm({ engagementType: 'Application Penetration Test' });
    setTimeout(() => setSubmitted(''), 6000);
    load();
  };

  // Someone who is not a requester sees the queue: what has been asked for and
  // is waiting to be picked up. Everything already under way lives on the
  // Engagements page, which is where they came from.
  const visible = isRequester ? engagements : engagements.filter(e => e.status === 'Requested');

  // The queue exists to be worked in order, so the tightest deadline comes
  // first. Requests with no deadline go last rather than first: no deadline is
  // the absence of urgency, not the maximum of it.
  const byDeadline = (a, b) => {
    const x = a.report_needed_by, y = b.report_needed_by;
    if (x && y) return new Date(x) - new Date(y);
    if (x) return -1;
    if (y) return 1;
    return 0;
  };

  const cancelRequest = async (row) => {
    if (!window.confirm(
      `Withdraw the request for "${row.name || row.engagement_type}"? ` +
      'The testing team will be told it is no longer wanted.'
    )) return;
    const res = await apiFetch(`/engagements/requests/${row.id}/cancel`, { method: 'POST' });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || `Could not withdraw the request (HTTP ${res.status})`);
      return;
    }
    setError('');
    load();
  };

  const filtered = visible.filter(e => {
    if (statusFilter && e.status !== statusFilter) return false;
    if (typeFilter && e.engagement_type !== typeFilter) return false;
    if (search) {
      const hay = [e.name, e.engagement_type, e.client_name, e.status, e.customer_tracking_id]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  }).sort(isRequester ? undefined : byDeadline);

  const columns = [
    { key: 'name', label: 'Test', render: (v, row) => (
      <div style={{ maxWidth: 340 }}>
        <div style={{ fontWeight: 600 }}>{v || row.engagement_type}</div>
        {row.scoping_comments && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {row.scoping_comments}
          </div>
        )}
      </div>
    )},
    { key: 'engagement_type', label: 'Type' },
    { key: 'status', label: 'Status', render: v => <Badge severity={v}>{v}</Badge> },
    { key: 'report_needed_by', label: 'Report Needed By', render: v => <NeededBy value={v} /> },
  ];
  if (needsClient) columns.splice(1, 0, { key: 'client_name', label: 'Client' });
  if (isRequester) {
    // Only while it is still waiting. Once it is being worked on, withdrawing
    // it is a conversation with the testing team rather than a button.
    columns.push({ key: 'id', label: '', render: (v, row) => (
      row.status === 'Requested' ? (
        <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={e => { e.stopPropagation(); cancelRequest(row); }}>
          Cancel Request
        </Button>
      ) : null
    )});
  }
  if (!isRequester) {
    columns.splice(columns.length - 1, 0,
      { key: 'requested_by_name', label: 'Requested By', render: v => v || '—' });
  }

  const awaiting = visible.filter(e => e.status === 'Requested').length;

  const selectStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)', background: 'var(--bg-input)',
    color: 'var(--text)', fontSize: 13, outline: 'none',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Requests</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {isRequester
              ? 'Ask for a penetration test, and follow the ones you have asked for.'
              : 'Tests that have been requested and are waiting to be scoped.'}
          </p>
        </div>
        <Button onClick={() => { setFormError(''); setShowForm(true); }}>Request a test</Button>
      </div>

      {submitted && (
        <Card style={{ marginBottom: 20, borderLeft: '3px solid #22c55e' }}>
          <strong style={{ fontSize: 14 }}>Request submitted: {submitted}</strong>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            It is now waiting to be scoped, and appears below as Requested. You will not be able to
            change it from here — the testing team will come back to you with dates and scope.
          </div>
        </Card>
      )}

      {error && (
        <Card style={{ marginBottom: 20, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
          {error}
        </Card>
      )}

      {isRequester && awaiting > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <strong style={{ fontSize: 14 }}>
            {awaiting} request{awaiting === 1 ? '' : 's'} waiting to be scoped
          </strong>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            A request stays in Requested until the testing team picks it up and agrees scope and dates.
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 20, padding: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: 12, alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4,
                            color: 'var(--text-secondary)' }}>Search</label>
            <input placeholder="Name, type, status..." value={search}
              onChange={e => setSearch(e.target.value)} style={selectStyle} />
          </div>
          {isRequester && (
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4,
                              color: 'var(--text-secondary)' }}>Status</label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
                <option value="">All statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4,
                            color: 'var(--text-secondary)' }}>Type</label>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selectStyle}>
              <option value="">All types</option>
              {ENGAGEMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(search || statusFilter || typeFilter) && (
              <Button variant="secondary" style={{ fontSize: 13 }}
                onClick={() => { setSearch(''); setStatusFilter(''); setTypeFilter(''); }}>
                Clear
              </Button>
            )}
            <Button variant="secondary" style={{ fontSize: 13 }} onClick={load}>Refresh</Button>
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 12 }}>
          Showing {filtered.length} of {visible.length}
          {!isRequester && filtered.length > 0 && ' — open a request to scope it'}
        </div>
      </Card>

      {loading ? (
        <Card style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading...</Card>
      ) : filtered.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <p style={{ fontSize: 16 }}>
            {engagements.length === 0
              ? (isRequester
                  ? 'You have not requested any tests yet. Use “Request a test” to ask for one.'
                  : 'Nothing is waiting to be scoped.')
              : 'No tests match these filters.'}
          </p>
        </Card>
      ) : (
        <Table columns={columns} data={filtered}
          onRowClick={isRequester ? undefined : (row => navigate(`/engagements/${row.id}`))} />
      )}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Request a penetration test" width={560}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
          Tell us what you would like tested and roughly when. The testing team will agree the scope
          and confirm dates with you — nothing here is a commitment.
        </p>
        {formError && (
          <div style={{ padding: 10, borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca',
                        color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{formError}</div>
        )}
        {needsClient && (
          <Select label="Client *" value={form.clientId || ''}
            onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}
            options={[{ value: '', label: '— Select Client —' },
                      ...clients.map(c => ({ value: c.id, label: c.name }))]} />
        )}
        <Select label="What kind of test?" value={form.engagementType}
          onChange={e => setForm(f => ({ ...f, engagementType: e.target.value }))}
          options={ENGAGEMENT_TYPES.map(t => ({ value: t, label: t }))} />
        {APPLICATION_TYPES.includes(form.engagementType) && (
          <Combobox label="Which application?"
            value={form.applicationId || ''}
            options={apps.map(a => ({
              value: a.id,
              label: a.customer_tracking_id ? `${a.name} — ${a.customer_tracking_id}` : a.name,
            }))}
            onChange={v => {
              const picked = apps.find(a => a.id === v);
              setForm(f => ({
                ...f,
                applicationId: v,
                // The application is the obvious answer to "what should be
                // tested", so it fills that in — still editable, since a
                // request might be for one part of a larger system.
                name: picked && !f.name ? picked.name : f.name,
              }));
            }}
            placeholder={apps.length ? 'Type to search applications...' : 'No applications listed for your organisation'}
            emptyText="No application matches that"
            disabled={apps.length === 0} />
        )}
        {APPLICATION_TYPES.includes(form.engagementType) && form.applicationId &&
          apps.find(a => a.id === form.applicationId) && (
            <ApplicationSummary app={apps.find(a => a.id === form.applicationId)} />
        )}
        <Input label="What should be tested? *" value={form.name || ''}
          placeholder="e.g. Customer payments portal"
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <TextArea label="Anything else we should know" value={form.description || ''}
          placeholder="Environments, deadlines, recent changes, who to talk to..."
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        <Input label="Report needed by" type="date" value={form.reportNeededBy || ''}
          onChange={e => setForm(f => ({ ...f, reportNeededBy: e.target.value }))} />
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: -8, marginBottom: 16 }}>
          When you need the finished report in hand, if there is a deadline driving this. It helps us
          decide what to pick up first. Testing dates are agreed separately once we scope the work.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !form.name?.trim()}>
            {submitting ? 'Submitting...' : 'Submit request'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
